import React, { memo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { AdaptiveSingleLineText, Icons, SpinnerOverlay, SubmissionButtons, TextArea } from '@components/common';

import { EValidationMode } from '@enums/validationMode';

import { descriptionSchema } from './schema';

import styles from './UploadContractDocuments.module.scss';

interface IUploadContractDocumentsProps {
	fileName: string;
	description: string;
	onSubmit: (description: string) => void;
	onClose: () => void;
	isLoading?: boolean;
	setIsDirtyForm: (isDirty: boolean) => void;
}

interface IFormValues {
	description: string;
}

const UploadContractDocuments = ({
	fileName,
	description = '',
	onSubmit,
	onClose,
	isLoading,
	setIsDirtyForm,
}: IUploadContractDocumentsProps) => {
	const {
		formState: { errors, isDirty },
		register,
		handleSubmit,
	} = useForm<IFormValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(descriptionSchema),
		defaultValues: {
			description,
		},
	});

	const handleUpload = useCallback(({ description }: IFormValues) => {
		onSubmit(description ? description : '');
	}, [onSubmit]);

	useEffect(() => setIsDirtyForm(isDirty), [isDirty, setIsDirtyForm]);

	return (
		<div className={styles.container}>
			{isLoading && <SpinnerOverlay />}
			<div className={styles.fileWrapper}>
				<span className={styles.fileIcon}>
					{Icons.fileIcon()}
				</span>
				<AdaptiveSingleLineText
					className={styles.fileName}
					maxWidth={200}
					paddingsSize={0}
					text={fileName}
				/>
			</div>
			<TextArea
				errorMessage={errors?.description?.message}
				isNotResizable
				label='Description'
				name='description'
				placeholder='Enter file description'
				register={register}
				rows={5}
				withUseForms
			/>
			<SubmissionButtons
				disabledPositive={isLoading}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(handleUpload)}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default memo(UploadContractDocuments);
